import { useState, useEffect } from 'react';
import { getCompanyGroup, CnpjSpotCompany, CnpjSpotGroupResponse } from '../../../services/CnpjSpotService';
import appConfig from '../../../config/app-config';

const { address: CAE_API_URL, key: CAE_API_KEY } = appConfig.apis.creditAnalysisApiEnv;

interface DraweeDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    document: string;
    itemId?: string | null;
}

const maskCnpj = (cnpj: string) => {
    const d = cnpj.replace(/\D/g, '');
    if (d.length !== 14) return cnpj;
    return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
};

const fmtCurrency = (v: number | null | undefined) => {
    if (v == null) return '-';
    return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const renderField = (label: string, value: string | number | null | undefined) => (
    <div className="py-2 border-b border-gray-200 dark:border-slate-600">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
            {value != null && value !== '' ? String(value) : <span className="text-gray-400 dark:text-gray-500 italic">-</span>}
        </div>
    </div>
);

// ── PJ: Company Card ──
const CompanyCard = ({ company, isParent }: { company: CnpjSpotCompany; isParent?: boolean }) => (
    <div className={`border rounded p-4 ${isParent ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-slate-700' : 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800'}`}>
        {isParent && <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase mb-2 block">Matriz</span>}
        {!isParent && <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 block">Filial</span>}
        <div className="grid grid-cols-2 gap-x-4">
            {renderField('CNPJ', maskCnpj(company.cnpj))}
            {renderField('Razão Social', company.razao_social)}
            {renderField('Nome Fantasia', company.nome_fantasia)}
            {renderField('Situação', company.descricao_situacao_cadastral)}
            {renderField('Capital Social', fmtCurrency(company.capital_social))}
            {renderField('Porte', company.porte)}
            {renderField('CNAE Principal', company.cnae_fiscal_descricao)}
            {renderField('UF', company.uf)}
            {renderField('Município', company.municipio)}
            {renderField('Logradouro', `${company.logradouro || ''}${company.numero ? `, ${company.numero}` : ''}`)}
        </div>
        {company.qsa && company.qsa.length > 0 && (
            <div className="mt-3">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase mb-2">Sócios ({company.qsa.length})</div>
                <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-300 dark:border-slate-500 text-xs text-gray-500 dark:text-gray-400">
                        <th className="text-left py-1 pr-2">Nome</th><th className="text-left py-1 pr-2">CPF/CNPJ</th><th className="text-left py-1">Qualificação</th>
                    </tr></thead>
                    <tbody>{company.qsa.map((s, i) => (
                        <tr key={i} className="border-b border-gray-100 dark:border-slate-600">
                            <td className="py-1.5 pr-2 text-gray-900 dark:text-gray-100">{s.nome_socio}</td>
                            <td className="py-1.5 pr-2 font-mono text-gray-700 dark:text-gray-300">{s.cnpj_cpf_do_socio || '-'}</td>
                            <td className="py-1.5 text-gray-700 dark:text-gray-300">{s.qualificacao_socio || '-'}</td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>
        )}
    </div>
);

// ── PF: Person Card from bureau registration data ──
const PersonCard = ({ reg }: { reg: any }) => (
    <div className="border border-green-300 dark:border-green-600 bg-green-50 dark:bg-slate-700 rounded p-4">
        <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase mb-2 block">Pessoa Física</span>
        <div className="grid grid-cols-2 gap-x-4">
            {renderField('Nome', reg.consumerName)}
            {renderField('CPF', reg.documentNumber)}
            {renderField('Nome da Mãe', reg.motherName)}
            {renderField('Data de Nascimento', reg.birthDate)}
            {renderField('Sexo', reg.consumerGenderDescription || reg.consumerGender)}
            {renderField('Situação CPF', reg.statusRegistration)}
            {renderField('Data Situação', reg.statusDate)}
        </div>
        {reg.address && (
            <div className="mt-3">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase mb-2">Endereço</div>
                <div className="grid grid-cols-2 gap-x-4">
                    {renderField('Logradouro', reg.address.addressLine)}
                    {renderField('Bairro', reg.address.district)}
                    {renderField('Cidade', reg.address.city)}
                    {renderField('UF', reg.address.state)}
                    {renderField('CEP', reg.address.zipCode)}
                </div>
            </div>
        )}
        {reg.phones && reg.phones.length > 0 && (
            <div className="mt-3">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase mb-2">Telefones</div>
                {reg.phones.slice(0, 3).map((p: any, i: number) => (
                    <div key={i} className="text-sm text-gray-700 dark:text-gray-300">
                        ({p.areaCode}) {p.phoneNumber} — {p.phoneType}
                    </div>
                ))}
            </div>
        )}
    </div>
);

export default function DraweeDetailsModal({ isOpen, onClose, document, itemId }: DraweeDetailsModalProps) {
    const cleanDoc = document.replace(/\D/g, '');
    const isCPF = cleanDoc.length === 11;

    // PJ state
    const [pjData, setPjData] = useState<CnpjSpotGroupResponse | null>(null);
    const [showBranches, setShowBranches] = useState(false);

    // PF state
    const [pfRegistration, setPfRegistration] = useState<any>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen || !document) return;
        setLoading(true);
        setError(null);
        setPjData(null);
        setPfRegistration(null);
        setShowBranches(false);

        if (isCPF) {
            // Fetch bureau data for PF
            if (!itemId) {
                setError('Dados não disponíveis para esta pessoa física no momento.');
                setLoading(false);
                return;
            }
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (CAE_API_KEY) headers['x-api-key'] = CAE_API_KEY;
            fetch(`${CAE_API_URL}/analysis-items/${itemId}/bureau-details`, { headers })
                .then(res => res.json())
                .then(data => {
                    const bureaus = data.bureaus || [];
                    // Find SERASA or ACRISCORE bureau with registration data
                    const serasa = bureaus.find((b: any) => (b.bureau_name || b.bureau || '').toUpperCase().includes('SERASA'));
                    const agri = bureaus.find((b: any) => (b.bureau_name || b.bureau || '').toUpperCase().includes('ACRISCORE'));
                    const source = agri || serasa;
                    const registration = source?.data?.registration || source?.data?.reports?.[0]?.registration;
                    if (registration) {
                        setPfRegistration(registration);
                    } else {
                        setError('Dados de pessoa física não disponíveis. Bureau ainda não foi consultado.');
                    }
                })
                .catch(() => setError('Erro ao buscar dados'))
                .finally(() => setLoading(false));
        } else {
            // PJ: fetch from CnpjSpot
            const root = cleanDoc.substring(0, 8);
            getCompanyGroup(root)
                .then(setPjData)
                .catch(err => setError(err.message || 'Erro ao buscar dados'))
                .finally(() => setLoading(false));
        }
    }, [isOpen, document, itemId]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                <div className="border-b border-gray-200 dark:border-slate-600 px-6 py-4 flex-shrink-0">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        Detalhes do Sacado
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{document} — {isCPF ? 'Pessoa Física' : 'Pessoa Jurídica'}</p>
                </div>

                <div className="px-6 py-4 overflow-y-auto flex-1">
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                            <span className="ml-3 text-gray-500 dark:text-gray-400">Carregando...</span>
                        </div>
                    )}

                    {error && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-4 text-yellow-700 dark:text-yellow-400 text-sm">
                            {error}
                        </div>
                    )}

                    {/* PF content */}
                    {pfRegistration && <PersonCard reg={pfRegistration} />}

                    {/* PJ content */}
                    {pjData && (
                        <div className="space-y-4">
                            {pjData.parent_company ? (
                                <CompanyCard company={pjData.parent_company} isParent />
                            ) : (
                                <div className="text-sm text-gray-500 dark:text-gray-400 italic">Matriz não encontrada</div>
                            )}
                            {pjData.branches.length > 0 && (
                                <div>
                                    <button
                                        onClick={() => setShowBranches(prev => !prev)}
                                        className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                                    >
                                        <span className={`transform transition-transform ${showBranches ? 'rotate-90' : ''}`}>▶</span>
                                        Filiais ({pjData.branches.length})
                                    </button>
                                    {showBranches && (
                                        <div className="mt-3 space-y-3">
                                            {pjData.branches.map(branch => (
                                                <CompanyCard key={branch.cnpj} company={branch} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="border-t border-gray-200 dark:border-slate-600 px-6 py-4 flex justify-end flex-shrink-0 bg-gray-50 dark:bg-slate-700">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-slate-600 text-gray-900 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-slate-500">
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
}
